import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MENU_ITEMS } from '../../utils/constants';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Helper function to get translated menu label
  const getMenuLabel = (item) => {
    return t(`menu.${item.id}`);
  };

  // Helper function to get translated submenu label
  const getSubMenuLabel = (subItem) => {
    return t(`menu.${subItem.id}`);
  };

    // Check if user is admin - only show Users menu to admin
  const isAdmin = user?.roles?.includes('ADMIN') || false;

  // Filter menu items based on user role
  const filteredMenuItems = MENU_ITEMS.filter(item => {
    if (item.id === 'users') {
      return isAdmin; // Only show Users menu to admin
    }
    return true; // All other menus are visible to all users
  });

  return (
    <div className={`bg-gray-900 text-white h-screen fixed left-0 top-0 transition-all duration-300 overflow-y-auto ${
      collapsed ? 'w-20' : 'w-72'
    }`}>
      {/* Logo */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">⚡</span>
          {!collapsed && <span className="font-bold text-xl">{t('app.name')}</span>}
        </div>
        <button onClick={() => setCollapsed(!collapsed)} className="p-1 hover:bg-gray-800 rounded">
          {collapsed ? '→' : '←'}
        </button>
      </div>

      {/* Dashboard */}
      <div className="mb-1 px-4">
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            `flex items-center space-x-3 px-4 py-3 transition-colors ${
              isActive ? 'text-white bg-indigo-600' : 'text-gray-300 hover:text-white hover:bg-gray-800'
            }`
          }
        >
          <span className="text-xl">📊</span>
          {!collapsed && <span className="text-sm font-medium">{t('menu.dashboard')}</span>}
        </NavLink>
      </div>

      {/* Profile - visible to ALL authenticated users */}
      {user && (
        <div className="mb-2 px-4">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `flex items-center space-x-3 px-4 py-3 transition-colors ${
                isActive ? 'text-white bg-indigo-600' : 'text-gray-300 hover:text-white hover:bg-gray-800'
              }`
            }
          >
            <span className="text-xl">👤</span>
            {!collapsed && <span className="text-sm font-medium">Profile</span>}
          </NavLink>
        </div>
      )}
      
      {/* Logout Button */}
      <div className="mb-2 px-4">
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-white hover:bg-red-600 rounded transition-colors"
        >
          <span className="text-xl">🚪</span>
          {!collapsed && <span className="text-sm font-medium">{t('app.logout')}</span>}
        </button>
      </div>

      {/* Separator */}
      {!collapsed && (
        <div className="my-2 px-4">
          <div className="border-t border-gray-700"></div>
        </div>
      )}

      {/* Other menu items */}
      {MENU_ITEMS.filter(item => item.id !== 'dashboard').map((item) => (
        <div key={item.id} className="mb-1">
          {item.submenu?.length > 0 ? (
            <>
              <button
                onClick={() => toggleMenu(item.id)}
                className={`w-full flex items-center justify-between px-4 py-3 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors ${
                  expandedMenus[item.id] ? 'bg-gray-800 text-white' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-xl">{item.icon}</span>
                  {!collapsed && <span className="text-sm font-medium">{getMenuLabel(item)}</span>}
                </div>
                {!collapsed && (
                  <span className="text-xs">
                    {expandedMenus[item.id] ? '▼' : '▶'}
                  </span>
                )}
              </button>

              {expandedMenus[item.id] && !collapsed && (
                <div className="bg-gray-800/50 py-1">
                  {item.submenu.map((subItem) => (
                    <NavLink
                      key={subItem.id}
                      to={subItem.path}
                      className={({ isActive }) =>
                        `flex items-center space-x-3 px-8 py-2 text-sm transition-colors ${
                          isActive ? 'text-white bg-indigo-600' : 'text-gray-400 hover:text-white hover:bg-gray-700'
                        }`
                      }
                    >
                      <span>{subItem.icon}</span>
                      <span>{getSubMenuLabel(subItem)}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </>
          ) : (
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 transition-colors ${
                  isActive ? 'text-white bg-indigo-600' : 'text-gray-300 hover:text-white hover:bg-gray-800'
                }`
              }
            >
              <span className="text-xl">{item.icon}</span>
              {!collapsed && <span className="text-sm font-medium">{getMenuLabel(item)}</span>}
            </NavLink>
          )}
        </div>
      ))}
    </div>
  );
};

export default Sidebar;